import { useEffect, useState } from "react";
import { OrderPlacementTabs } from "../../components/OrderPlacementTabs/OrderPlacementTabs";
import { fetchBranches, fetchCities } from "../../services/novaPoshta";
import { createInvoice, createInvoiceEng } from "../../services/monobank";
import { PaypalButton } from "../../components/PaypalButton/PaypalButton";
import { PAYPAL_FEE_PERCENT } from "../../services/paypal";
import { roundToHigherOrder } from "../../utils/roundToHigherOrder";
import { useTranslation } from "react-i18next";
import { Sizes } from "../../interfaces/sizes";
import { toast } from "react-toastify";
import {
  calculateDeliveryTotal,
  calculateDeliveryTotalUsd,
} from "../../utils/calculateDeliveryTotal";
import { useLocation, useNavigate } from "react-router-dom";
import { Processing } from "../../components/Processing/Processing";
import { Title } from "../../components/Title/Title";
import { checkPromocode } from "../../services/axios";
import { Promocode } from "../../interfaces/promocode";
import { calculateDiscount } from "../../utils/promocode";

export const OrderPlacement = () => {
  const { state } = useLocation();
  const navigate = useNavigate();

  const [processing, setProcessing] = useState(false);

  const {
    setOrder,
    order,
    userCart,
    quantities,
    userCartSizes,
    total,
    exchangeRate,
  } = state || {};

  useEffect(() => {
    if (!state) {
      navigate("/");
    }
  }, [state, navigate]);

  useEffect(() => {
    const invoiceId = localStorage.getItem("invoiceId");

    if (invoiceId) {
      setProcessing(true);
    }
  }, []);

  const [paymentTotal, setPaymentTotal] = useState(total);
  const [activeTab, setActiveTab] = useState<"ukraine" | "abroad">("ukraine");
  const [bankPayment, setBankPayment] = useState(true);
  const [paypalPayment, setPaypalPayment] = useState(false);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [abroadCity, setAbroadCity] = useState("");
  const [country, setCountry] = useState("");
  const [street, setStreet] = useState("");
  const [zip, setZip] = useState("");
  const [selectedCity, setSelectedCity] = useState(null);
  const [cities, setCities] = useState([]);
  const [branches, setBranches] = useState([]);
  const [filteredBranches, setFilteredBranches] = useState([]);
  const [city, setCity] = useState("");
  const [branch, setBranch] = useState("");
  const [promocodeInput, setPromocodeInput] = useState("");
  const [promocode, setPromocode] = useState<Promocode | null>(null);

  const { i18n, t } = useTranslation();

  const countTotal = () => {
    let total = 0;
    let index = 0;
    if (userCart?.length > 0) {
      for (const { usd, uah } of userCart) {
        if (i18n.language === "uk") {
          total +=
            uah !== 0
              ? roundToHigherOrder(uah * quantities[index])
              : roundToHigherOrder(usd * exchangeRate * quantities[index]);
          index += 1;
        } else {
          total += usd * quantities[index];
        }
      }
    }
    return total;
  };

  useEffect(() => {
    const isEnglish = i18n.language === "en";

    setActiveTab(isEnglish ? "abroad" : "ukraine");
    // english customers are almost always abroad, so PayPal is the default
    setPaypalPayment(isEnglish);
    // cash on delivery exists only inside Ukraine
    if (isEnglish) {
      setBankPayment(true);
    }
    setPaymentTotal(countTotal());
  }, [i18n.language]);

  const getAllCities = async (searchQuery: string) => {
    setCities(await fetchCities(searchQuery));
  };

  const getAllBranches = async (cityRef: any) => {
    setBranches(await fetchBranches(cityRef));
  };
  const handleCityChange = (e: any) => {
    setCity(e.target.value);
    setSelectedCity(null);
    setBranches([]);
    setFilteredBranches([]);
    if (e.target.value.length > 2) {
      getAllCities(e.target.value);
    } else {
      setCities([]);
    }
  };

  // counted from the cart itself: "userCartLimits" is a running counter that
  // drifts away from the real contents, and the server prices delivery from
  // the products it receives, so both sides have to count the same way
  const cart = (userCart || []).reduce(
    (acc: any, product: any, index: number) => {
      const type = product?.type;

      if (type && acc[type] !== undefined) {
        acc[type] += quantities?.[index] || 0;
      }

      return acc;
    },
    { merch: 0, vinyl: 0, cassette: 0, cd: 0 }
  );

  const handleCitySelect = (city: any) => {
    setCity(city.Description);
    setSelectedCity(city);
    setCities([]);
    setBranches([]);
    setBranch("");
    getAllBranches(city.Ref);
  };

  const handleBranchChange = (e: any) => {
    if (city && selectedCity) {
      if (selectedCity) {
        const inputValue = e.target.value;
        setBranch(inputValue);

        const filtered = branches.filter((branch: any) =>
          branch?.Description.toLowerCase().includes(inputValue.toLowerCase())
        );
        setFilteredBranches(filtered);
      }
    }
  };

  const handleBranchSelect = (branch: any) => {
    setBranch(branch.Description);
    setBranches([]);
    setFilteredBranches([]);
  };

  const deliveryTotal =
    activeTab === "ukraine"
      ? 0
      : i18n.language === "uk"
        ? calculateDeliveryTotal(
            cart.merch,
            cart.vinyl,
            cart.cassette,
            cart.cd,
            exchangeRate
          )
        : calculateDeliveryTotalUsd(
            cart.merch,
            cart.vinyl,
            cart.cassette,
            cart.cd
          );

  const discount = Math.min(
    calculateDiscount(
      promocode,
      userCart,
      quantities,
      i18n.language,
      exchangeRate
    ),
    Math.max(paymentTotal + deliveryTotal - 1, 0)
  );

  const totalBeforeFee = Math.max(paymentTotal + deliveryTotal - discount, 0);

  // PayPal takes a processing fee, which the customer pays on top
  const rawPaypalFee = paypalPayment
    ? (totalBeforeFee * PAYPAL_FEE_PERCENT) / 100
    : 0;
  const paypalFee =
    i18n.language === "uk"
      ? Math.round(rawPaypalFee)
      : Math.round(rawPaypalFee * 100) / 100;

  const finalTotal =
    Math.round((totalBeforeFee + paypalFee) * 100) / 100;

  // shared by the monobank flow and the PayPal buttons
  const buildCheckoutData = () => {
    const filled =
      name &&
      phone &&
      email &&
      ((activeTab === "ukraine" && city && branch) ||
        (activeTab === "abroad" && abroadCity && country && street && zip));

    if (!filled) {
      return null;
    }

    const products: any = [];
    const userCartColors = JSON.parse(
      localStorage.getItem("userCartColors") ?? "[]"
    );
    let index = 0;
    for (const product of userCart) {
      const obj: any = {
        id: product._id,
        amount: quantities[index],
        title:
          product.type === "merch"
            ? `${product.name} - ${product.from}`
            : `${product.artistName} - ${product.albumName}`,
        availability: product?.availability,
        type: product?.type,
        color: userCartColors[index] || "",
      };
      if (product.type === "merch") {
        obj.size =
          product.info[0].oneSizeAmount > 0 ? "" : userCartSizes[index];
      }
      products.push(obj);

      index += 1;
    }

    const message =
      `
НОВЕ ЗАМОВЛЕННЯ НА САЙТІ

ПІБ: ${name}
E-mail: ${email}
Номер телефону: ${phone}

Метод оплати: ${
        paypalPayment
          ? "PayPal"
          : bankPayment
            ? "Банківський переказ"
            : "Наложенний платіж"
      }
${promocode ? `Промокод: ${promocode.code} (-${discount})` : ""}
${paypalFee > 0 ? `Комісія PayPal: +${paypalFee}` : ""}

Доставка:
${
  activeTab === "abroad"
    ? `- Вартість: ${deliveryTotal}${i18n.language === "uk" ? " ₴" : " $"}`
    : ""
}
- Служба: ${activeTab === "abroad" ? "Укрпошта" : "Нова Пошта"}
${activeTab === "abroad" ? `- Країна: ${country}` : ""}
${
  activeTab === "ukraine"
    ? `- Населений пункт: ${city}`
    : `- Населений пункт: ${abroadCity}`
}
${activeTab === "ukraine" ? `- Відділення: ${branch}` : ""}
${activeTab === "abroad" ? `- Вулиця: ${street}` : ""}
${activeTab === "abroad" ? `- Поштовий індекс: ${zip}` : ""}
Замовлення:
` + order;

    return { products, message };
  };

  const getPaypalPayload = () => {
    const data = buildCheckoutData();

    if (!data) {
      toast(t("CartFillTheForm"), {
        className: "toast__error",
        bodyClassName: "toast__body",
        progressClassName: "toast__progressBar--error",
      });
      return null;
    }

    return {
      city,
      branch,
      country,
      abroadCity,
      address: street,
      index: zip,
      products: data.products,
      name,
      phone,
      email,
      message: data.message,
      activeTab,
      lang: i18n.language,
      promocode: promocode?.code || "",
    };
  };

  const applyPromocode = async () => {
    if (!promocodeInput) {
      setPromocode(null);
      return;
    }

    const data = await checkPromocode(
      promocodeInput,
      Math.round(paymentTotal),
      i18n.language === "uk" ? "uah" : "usd"
    );

    if (data?.valid) {
      setPromocode(data.promocode);
      toast(t("CartPromocodeApplied"), {
        className: "toast__success",
        bodyClassName: "toast__body",
        progressClassName: "toast__progressBar--success",
      });
    } else {
      setPromocode(null);
      toast(
        data?.reason === "minPrice"
          ? `${t("CartPromocodeMinPrice")} ${data.minPrice} ${
              data.currency === "usd" ? "$" : "₴"
            }`
          : data?.reason === "tooManyRequests"
            ? t("CartPromocodeTooMany")
            : t("CartPromocodeInvalid"),
        {
          className: "toast__error",
          bodyClassName: "toast__body",
          progressClassName: "toast__progressBar--error",
        }
      );
    }
  };

  return (
    <div>
      <div className="order-placement">
        <div className="container__two">
          <Title title={t("OrderPlacementTitle")} type={false} />
          <div className="order-placement__wrapper">
            <p className="order-placement__title" style={{ margin: 0 }}>
              {t("CartData")}
            </p>
            <div className="order-placement__thumb">
              <p className="order-placement__subtitle">{t("CartFullName")}:</p>
              <input
                type="text"
                value={name}
                onChange={(e) => {
                  const regex = /^[\u0400-\u04FFa-zA-Z\s]*$/;

                  if (regex.test(e.target.value)) {
                    setName(e.target.value);
                  }
                }}
                className="order-placement__input"
                placeholder={t("CartFullName")}
              />
            </div>
            <div className="order-placement__thumb">
              <p className="order-placement__subtitle">{t("CartPhone")}</p>
              <input
                type="text"
                value={phone}
                onChange={(e) => {
                  const inputValue = e.target.value;
                  const sanitizedValue = inputValue.replace(/[^0-9]/g, "");

                  if (activeTab === "ukraine") {
                    if (
                      sanitizedValue.length === 0 ||
                      sanitizedValue.length <= 10
                    ) {
                      setPhone(sanitizedValue);
                    }
                  } else {
                    setPhone(sanitizedValue);
                  }
                }}
                className="order-placement__input"
                placeholder={t("CartPhonePlaceholder")}
              />
            </div>
            <div className="order-placement__thumb">
              <p className="order-placement__subtitle">{t("CartEmail")}</p>
              <input
                type="text"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                }}
                className="order-placement__input"
                placeholder={t("CartEmailPlaceholder")}
              />
            </div>
            <p className="order-placement__title">{t("CartDeliveryMethod")}</p>
            <OrderPlacementTabs
              activeTab={activeTab}
              setActiveTab={setActiveTab}
              setBankPayment={setBankPayment}
            />
            {activeTab === "ukraine" ? (
              <>
                <div className="order-placement__thumb">
                  <p className="order-placement__subtitle">
                    {t("CartSettlement")}:
                  </p>
                  <div className="order-placement__select--wrapper">
                    <input
                      type="text"
                      value={city}
                      onChange={handleCityChange}
                      className="order-placement__input"
                      placeholder={t("CartCityPlaceholder")}
                    />
                    {cities.length > 0 && (
                      <ul className="order-placement__select--list">
                        {cities.map((city: any) => (
                          <li
                            key={city?.Ref}
                            onClick={() => handleCitySelect(city)}
                            className="order-placement__select--item"
                          >
                            {city.Description}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
                <div className="order-placement__thumb">
                  <p className="order-placement__subtitle">
                    {t("CartBranch")}:
                  </p>
                  <div className="order-placement__select--wrapper">
                    <input
                      type="branch"
                      value={branch}
                      onChange={handleBranchChange}
                      className="order-placement__input"
                      placeholder={t("CartBranchPlaceholder")}
                    />
                    {selectedCity && filteredBranches.length > 0 && (
                      <ul className="order-placement__select--list">
                        {filteredBranches?.map((branch: any) => (
                          <li
                            key={branch.Ref}
                            onClick={() => handleBranchSelect(branch)}
                            className="order-placement__select--item"
                          >
                            {branch.Description}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              </>
            ) : (
              <>
                <div className="order-placement__thumb">
                  <p className="order-placement__subtitle">
                    {t("CartCountry")}:
                  </p>
                  <input
                    type="text"
                    value={country}
                    onChange={(e) => {
                      const regex = /^[\u0400-\u04FFa-zA-Z\s]*$/;

                      if (regex.test(e.target.value)) {
                        if (e.target.value === " ") {
                        } else {
                          setCountry(e.target.value);
                        }
                      }
                    }}
                    className="order-placement__input"
                    placeholder={t("CartCountryPlaceholder")}
                  />
                </div>
                <div className="order-placement__thumb">
                  <p className="order-placement__subtitle">
                    {t("CartAbroadCity")}:
                  </p>
                  <input
                    type="text"
                    value={abroadCity}
                    onChange={(e) => {
                      const regex = /^[\u0400-\u04FFa-zA-Z\s]*$/;

                      if (regex.test(e.target.value)) {
                        if (e.target.value === " ") {
                        } else {
                          setAbroadCity(e.target.value);
                        }
                      }
                    }}
                    className="order-placement__input"
                    placeholder={t("CartAbroadCityPlaceholder")}
                  />
                </div>
                <div className="order-placement__thumb">
                  <p className="order-placement__subtitle">
                    {t("CartAddress")}:
                  </p>
                  <input
                    type="text"
                    value={street}
                    onChange={(e) => {
                      if (e.target.value === " ") {
                      } else {
                        setStreet(e.target.value);
                      }
                    }}
                    className="order-placement__input"
                    placeholder={t("CartAddressPlaceholder")}
                  />
                </div>
                <div className="order-placement__thumb">
                  <p className="order-placement__subtitle">
                    {t("CartPostcode")}:
                  </p>
                  <input
                    type="text"
                    value={zip}
                    onChange={(e) => {
                      if (e.target.value === " ") {
                      } else {
                        setZip(e.target.value);
                      }
                    }}
                    className="order-placement__input"
                    placeholder={t("CartPostcodePlaceholder")}
                  />
                </div>
              </>
            )}
            <p className="order-placement__title">{t("CartPaymentMethod")}</p>
            {activeTab === "ukraine" ? (
              <>
                <div className="order-placement__payment">
                  <div
                    onClick={() => {
                      setBankPayment(true);
                      setPaypalPayment(false);
                    }}
                    className={
                      bankPayment && !paypalPayment
                        ? "order-placement__payment--box order-placement__payment--boxActive"
                        : "order-placement__payment--box"
                    }
                  ></div>
                  <p
                    className="order-placement__subtitle"
                    style={{ display: "block" }}
                  >
                    {t("CartPaymentMethodDescriptionFirst")}
                  </p>
                </div>
                <div className="order-placement__payment">
                  <div
                    onClick={() => {
                      setBankPayment(false);
                      setPaypalPayment(false);
                    }}
                    className={
                      !bankPayment && !paypalPayment
                        ? "order-placement__payment--box order-placement__payment--boxActive"
                        : "order-placement__payment--box"
                    }
                  ></div>
                  <p
                    className="order-placement__subtitle"
                    style={{ display: "block" }}
                  >
                    {t("CartPaymentMethodDescriptionSecond")}
                  </p>
                </div>
                <div className="order-placement__payment">
                  <div
                    onClick={() => {
                      setBankPayment(true);
                      setPaypalPayment(true);
                    }}
                    className={
                      paypalPayment
                        ? "order-placement__payment--box order-placement__payment--boxActive"
                        : "order-placement__payment--box"
                    }
                  ></div>
                  <p
                    className="order-placement__subtitle"
                    style={{ display: "block" }}
                  >
                    {t("CartPaymentMethodPayPal")}
                  </p>
                </div>
              </>
            ) : (
              <>
                <p
                  className="order-placement__subtitle"
                  style={{ display: "block" }}
                >
                  {t("CartPaymentMethodDescriptionThird")}
                  {`${deliveryTotal}${i18n.language === "uk" ? " ₴" : " $"}`}
                </p>
                <div className="order-placement__payment">
                  <div
                    onClick={() => {
                      setPaypalPayment(false);
                    }}
                    className={
                      paypalPayment
                        ? "order-placement__payment--box"
                        : "order-placement__payment--box order-placement__payment--boxActive"
                    }
                  ></div>
                  <p
                    className="order-placement__subtitle"
                    style={{ display: "block" }}
                  >
                    {t("CartPaymentMethodDescriptionFirst")}
                  </p>
                </div>
                <div className="order-placement__payment">
                  <div
                    onClick={() => {
                      setPaypalPayment(true);
                    }}
                    className={
                      paypalPayment
                        ? "order-placement__payment--box order-placement__payment--boxActive"
                        : "order-placement__payment--box"
                    }
                  ></div>
                  <p
                    className="order-placement__subtitle"
                    style={{ display: "block" }}
                  >
                    {t("CartPaymentMethodPayPal")}
                  </p>
                </div>
              </>
            )}

            <p className="order-placement__title">{t("CartPromocode")}</p>
            <div className="order-placement__promo">
              <input
                type="text"
                value={promocodeInput}
                maxLength={12}
                onChange={(e) => {
                  setPromocodeInput(e.target.value.toUpperCase());
                  setPromocode(null);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    applyPromocode();
                  }
                }}
                className="order-placement__input order-placement__input--promo"
                placeholder="XXXXXXXXXX"
              />
              <button
                type="button"
                className="order-placement__promo--button button__hovering"
                onClick={applyPromocode}
              >
                {t("CartPromocodeApply")}
              </button>
            </div>

            <div className="order-placement__line"></div>
            {deliveryTotal > 0 && (
              <p className="order-placement__total">
                {t("CartDelivery")}:{" "}
                <span className="order-placement__sum">
                  {deliveryTotal}
                  {t("Currency")}
                </span>
              </p>
            )}
            {discount > 0 && (
              <p className="order-placement__total">
                {t("CartPromocode")}: -
                <span className="order-placement__sum">
                  {discount}
                  {t("Currency")}
                </span>
              </p>
            )}
            {paypalFee > 0 && (
              <p className="order-placement__total">
                {t("CartPaypalFee")}:{" "}
                <span className="order-placement__sum">
                  +{paypalFee}
                  {t("Currency")}
                </span>
              </p>
            )}
            <p className="order-placement__total">
              {t("CartInTotal")}:{" "}
              <span className="order-placement__total--accent">
                {finalTotal}
                {t("Currency")}
              </span>
            </p>
            {paypalPayment ? (
              <PaypalButton
                getPayload={getPaypalPayload}
                onSuccess={(invoiceId) => {
                  localStorage.removeItem("userCartColors");
                  localStorage.removeItem("userCartId");
                  localStorage.removeItem("userCartIds");
                  localStorage.removeItem("userCartQuantities");
                  localStorage.removeItem("userCartSizes");
                  localStorage.removeItem("userCartTypes");
                  localStorage.removeItem("userCartLimits");
                  navigate(
                    `/paypal/return?status=success&invoiceId=${invoiceId}`
                  );
                }}
                onFailure={(reason) => {
                  if (reason === "invalidForm") {
                    return;
                  }
                  toast(
                    reason === "cancelled"
                      ? t("ProcessingCancelledTitle")
                      : t("CartPayPalError"),
                    {
                      className: "toast__error",
                      bodyClassName: "toast__body",
                      progressClassName: "toast__progressBar--error",
                    }
                  );
                }}
              />
            ) : (
              <button
                className="order-placement__button button__hovering"
                onClick={async () => {
                  const checkout = buildCheckoutData();

                  if (checkout) {
                    const { products, message } = checkout;

                    if (activeTab === "ukraine") {
                      toast("Опрацьовуємо замовлення", {
                        className: "toast__success",
                        bodyClassName: "toast__body",
                        progressClassName: "toast__progressBar--success",
                      });
                      const data = await createInvoice(
                        city,
                        branch,
                        bankPayment
                          ? "Банківський переказ"
                          : "Наложенний платіж",
                        products,
                        name,
                        phone,
                        email,
                        message,
                        activeTab,
                        i18n.language,
                        promocode?.code || ""
                      );
                      if (data) {
                        if (bankPayment) {
                          setProcessing(false);
                          setTimeout(() => {
                            setProcessing(true);
                          });
                        } else {
                          toast("Замовлення успішне!", {
                            className: "toast__success",
                            bodyClassName: "toast__body",
                            progressClassName: "toast__progressBar--success",
                          });
                          setTimeout(() => {
                            localStorage.removeItem("invoiceLink");
                            localStorage.removeItem("invoiceId");
                            localStorage.removeItem("userCartColors");
                            localStorage.removeItem("userCartId");
                            localStorage.removeItem("userCartIds");
                            localStorage.removeItem("userCartQuantities");
                            localStorage.removeItem("userCartSizes");
                            localStorage.removeItem("userCartTypes");
                            window.location.href = "/";
                          }, 1000);
                        }
                      }
                    } else {
                      const data = await createInvoiceEng(
                        country,
                        abroadCity,
                        street,
                        zip,
                        bankPayment
                          ? "Банківський переказ"
                          : "Наложенний платіж",
                        products,
                        name,
                        phone,
                        email,
                        message,
                        activeTab,
                        i18n.language,
                        promocode?.code || ""
                      );
                      if (data) {
                        setProcessing(false);
                        setTimeout(() => {
                          setProcessing(true);
                        });
                      }
                    }
                  }
                }}
              >
                {t("CartPay")}
              </button>
            )}
          </div>
        </div>
      </div>
      {processing && <Processing setProcessing={setProcessing} />}
    </div>
  );
};
